import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Eye, Plus, Search } from 'lucide-react';

import Button from '@/shared/components/Button';
import Input from '@/shared/components/Input';
import Select from '@/shared/components/Select';
import '@/styles/design-system.css';

import {
  ROUTE_BACK_OFFICE_INSTRUCTION_BLOCK_EDIT,
  ROUTE_BACK_OFFICE_INSTRUCTION_BLOCK_NEW,
  ROUTE_BACK_OFFICE_RESOLVED_PROMPTS,
} from '../../../constants';
import { ENTITY_NAME_BACK_OFFICE_INSTRUCTION_BLOCK } from '../../../dao/dbConstants';
import { searchEntityRecords } from '../../../utils/entityApiUtils';
import { SERVER_URL } from '../../../utils/apiUtilsConstants';
import { showErrorMessage } from '@/shared/components/toast-system';

const SCOPE_FILTER_OPTIONS = [
  { value: 'common', label: 'Common' },
  { value: 'conditional', label: 'Conditional' },
];

const OPERATOR_LABELS = { equals: 'is', not_equals: 'is not', in: 'in' };

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const InstructionBlocks = () => {
  const navigate = useNavigate();

  const [blocks, setBlocks] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [searchResult, metaRes] = await Promise.all([
          searchEntityRecords(ENTITY_NAME_BACK_OFFICE_INSTRUCTION_BLOCK, {
            pageNumber: 1,
            pageSize: 1000,
            ignoreLimit: true,
            orderBy: 'sortOrder',
            sortOrderAscending: true,
          }),
          fetch(`${SERVER_URL}/api/ui/bo/instruction-blocks/meta`, { credentials: 'include' }),
        ]);
        setBlocks(Array.isArray(searchResult?.data?.list) ? searchResult.data.list : []);
        if (metaRes.ok) {
          const meta = await metaRes.json();
          setVerticals(meta?.verticals ?? []);
        }
      } catch (error) {
        console.error('InstructionBlocks: failed to load', error);
        showErrorMessage('Failed to load instruction blocks.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const verticalNameById = useMemo(() => {
    const map = new Map();
    verticals.forEach((v) => map.set(Number(v.id), v.name));
    return map;
  }, [verticals]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return blocks.filter((block) => {
      const isConditional = (block.scopeRules?.length ?? 0) > 0;
      if (scopeFilter === 'common' && isConditional) return false;
      if (scopeFilter === 'conditional' && !isConditional) return false;
      if (!needle) return true;
      return (
        (block.name ?? '').toLowerCase().includes(needle) ||
        (block.description ?? '').toLowerCase().includes(needle) ||
        (block.contentTypeKeys ?? []).join(' ').toLowerCase().includes(needle)
      );
    });
  }, [blocks, query, scopeFilter]);

  const conditionLabel = (condition) => {
    const resolve = (value) =>
      condition.field === 'vertical'
        ? verticalNameById.get(Number(value)) ?? value
        : value;
    const value = Array.isArray(condition.value)
      ? condition.value.map(resolve).join(', ')
      : resolve(condition.value);
    const field = condition.field === 'vertical' ? 'Vertical' : 'Intent';
    return `${field} ${OPERATOR_LABELS[condition.operator] ?? condition.operator} ${value}`;
  };

  const renderScopeBadge = (block) => {
    if (!block.scopeRules?.length) {
      return (
        <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
          Common
        </span>
      );
    }
    return (
      <span className="inline-flex flex-wrap items-center gap-1">
        {block.scopeRules.map((group, groupIndex) => (
          <span key={groupIndex} className="inline-flex items-center gap-1">
            {groupIndex > 0 && (
              <span className="text-[10px] font-semibold uppercase text-muted-foreground">or</span>
            )}
            <span className="inline-flex items-center rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-700">
              {group.map(conditionLabel).join(' AND ')}
            </span>
          </span>
        ))}
      </span>
    );
  };

  return (
    <div className="th !overflow-visible">
      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="mr-auto">
            <h2 className="text-xl font-semibold text-foreground">Instruction Blocks</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Reusable instructions appended to generation prompts — common, or conditional on vertical and intent.
            </p>
          </div>
          <Input
            icon={<Search className="size-4 text-muted-foreground" />}
            placeholder="Search blocks..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            wrapperClassName="w-64"
          />
          <div className="w-40">
            <Select
              value={scopeFilter}
              onValueChange={setScopeFilter}
              options={SCOPE_FILTER_OPTIONS}
              placeholder="All scopes"
              clearable
              clearLabel="All scopes"
            />
          </div>
          <Button
            variant="secondary"
            leftIcon={<Eye className="size-4" />}
            onClick={() => navigate(ROUTE_BACK_OFFICE_RESOLVED_PROMPTS)}
          >
            Resolved Prompt Preview
          </Button>
          <Button
            leftIcon={<Plus className="size-4" />}
            onClick={() => navigate(ROUTE_BACK_OFFICE_INSTRUCTION_BLOCK_NEW)}
          >
            Add Block
          </Button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground">Loading instruction blocks...</div>
        ) : (
          <div className="overflow-hidden !rounded-xl !border !border-solid !border-[#cbd5e1] !bg-white !shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="!border-b !border-solid !border-[#cbd5e1] !bg-[#f8fafc] text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Scope</th>
                  <th className="px-4 py-3">Content Type</th>
                  <th className="px-4 py-3 w-20">Sort</th>
                  <th className="px-4 py-3 w-24">Status</th>
                  <th className="px-4 py-3 w-32">Updated</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((block) => (
                  <tr
                    key={block.id}
                    onClick={() => navigate(`${ROUTE_BACK_OFFICE_INSTRUCTION_BLOCK_EDIT}${block.id}`)}
                    className="cursor-pointer !border-b !border-solid !border-[#e2e8f0] last:!border-b-0 transition-colors hover:!bg-[#eef2f6]"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{block.name}</div>
                      {block.description && (
                        <div className="mt-0.5 text-xs text-muted-foreground">{block.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">{renderScopeBadge(block)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {block.contentTypeKeys?.length ? block.contentTypeKeys.join(', ') : 'All content types'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{block.sortOrder ?? 0}</td>
                    <td className="px-4 py-3">
                      {block.isActive ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                          <span className="size-1.5 rounded-full bg-emerald-500" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                          <span className="size-1.5 rounded-full bg-slate-300" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(block.updatedOn)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <ChevronRight className="size-4" />
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      No instruction blocks found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructionBlocks;
